/* GENX Glass — interaction layer for the Liquid-Glass design.
   theme.js still owns the cart, gallery, menus and sticky CTA; this file only
   adds motion, light and morphing on top of the state classes theme.js sets. */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  if (new URLSearchParams(location.search).get('glass') === 'lite') root.classList.add('glass-lite');
  var lite = root.classList.contains('glass-lite');

  function isChromium() {
    var brands = navigator.userAgentData && navigator.userAgentData.brands;
    return !!brands && brands.some(function (b) { return /Chromium|Google Chrome|Microsoft Edge|Opera|Brave/i.test(b.brand); });
  }

  function loadScript(src) {
    if (!src || doc.querySelector('script[src="' + src + '"]')) return;
    var s = doc.createElement('script');
    s.src = src;
    s.defer = true;
    doc.head.appendChild(s);
  }

  /* ── pointer shine + rim light on glass ─────────────────── */
  var shineEvent = null;
  var shineFrame = 0;
  function applyShine() {
    shineFrame = 0;
    var e = shineEvent;
    var el = e && e.target && e.target.closest ? e.target.closest('.lg') : null;
    if (!el) return;
    var r = el.getBoundingClientRect();
    var x = e.clientX - r.left;
    var y = e.clientY - r.top;
    el.style.setProperty('--mx', (x / r.width * 100).toFixed(1) + '%');
    el.style.setProperty('--my', (y / r.height * 100).toFixed(1) + '%');
    var angle = Math.atan2(y - r.height / 2, x - r.width / 2) * 180 / Math.PI + 90;
    el.style.setProperty('--lg-angle', angle.toFixed(0) + 'deg');
  }
  if (canHover.matches && !lite) {
    doc.addEventListener('pointermove', function (e) {
      shineEvent = e;
      if (!shineFrame) shineFrame = requestAnimationFrame(applyShine);
    }, { passive: true });
  }

  /* ── press: gel squash + glow from the touch point ─────── */
  var PRESSABLE = '.btn, .lg-press';
  var pressed = null;
  doc.addEventListener('pointerdown', function (e) {
    var el = e.target.closest && e.target.closest(PRESSABLE);
    if (!el) return;
    var r = el.getBoundingClientRect();
    el.style.setProperty('--px', (e.clientX - r.left) + 'px');
    el.style.setProperty('--py', (e.clientY - r.top) + 'px');
    el.classList.add('is-pressed');
    pressed = el;
  }, { passive: true });
  function release() {
    if (!pressed) return;
    pressed.classList.remove('is-pressed');
    pressed = null;
  }
  ['pointerup', 'pointercancel', 'dragstart', 'blur'].forEach(function (type) {
    window.addEventListener(type, release, { passive: true });
  });

  /* ── one frame loop for every scroll-linked effect ────────
     Effects register read() (measure only) and write() (change only). Each frame runs
     the smooth-scroll step, then every read, then every write, so the page is measured
     once per frame and the parallax never lags the page. */
  var effects = [];
  var ticking = false;
  function requestTick() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(tick);
  }
  function tick(now) {
    ticking = false;
    var moving = stepSmooth(now);
    var i;
    for (i = 0; i < effects.length; i++) effects[i].read();
    for (i = 0; i < effects.length; i++) effects[i].write();
    if (moving) requestTick();
  }
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  /* ── smooth scroll for wheel and trackpad ──────────────────
     Wheel input sets a target and the page eases toward it (frame-rate independent),
     the way Apple's product pages glide. The page still scrolls natively, so sticky
     sections, anchors and the scrollbar keep working. Touch keeps the phone's own
     momentum; keyboard, scrollbar drags and find-in-page stay native and re-sync.
     Off with reduced motion, in the theme editor, while a sheet locks the page, and
     with ?scroll=native. */
  var smooth = {
    on: canHover.matches && !reduceMotion.matches && !(window.Shopify && window.Shopify.designMode) &&
      new URLSearchParams(location.search).get('scroll') !== 'native',
    target: 0,
    current: 0,
    moving: false,
    last: 0
  };
  var EASE = 0.1; // share of the remaining distance covered per 60 fps frame

  function maxScroll() { return Math.max(0, root.scrollHeight - window.innerHeight); }

  // a scrollable box under the pointer (cart list, menu, wide tables) keeps its own wheel
  function scrollsInside(el, dy) {
    for (; el && el !== doc.body && el !== root; el = el.parentElement) {
      if (el.scrollHeight <= el.clientHeight + 1) continue;
      var oy = getComputedStyle(el).overflowY;
      if (oy !== 'auto' && oy !== 'scroll') continue;
      if (dy < 0 ? el.scrollTop > 0 : el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
    }
    return false;
  }

  function stepSmooth(now) {
    if (!smooth.moving) {
      smooth.last = 0;
      return false;
    }
    var dt = smooth.last ? Math.min(64, now - smooth.last) : 16.67;
    smooth.last = now;
    smooth.target = Math.min(smooth.target, maxScroll());
    smooth.current += (smooth.target - smooth.current) * (1 - Math.pow(1 - EASE, dt / 16.67));
    if (Math.abs(smooth.target - smooth.current) < 0.4) {
      smooth.current = smooth.target;
      smooth.moving = false;
    }
    window.scrollTo(0, smooth.current);
    return smooth.moving;
  }

  // glide to a page position (rail ticks, in-page links)
  function scrollToY(y) {
    if (!smooth.on) {
      window.scrollTo({ top: y, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      return;
    }
    if (!smooth.moving) smooth.current = window.scrollY;
    smooth.target = clamp(y, 0, maxScroll());
    smooth.moving = true;
    requestTick();
  }

  if (smooth.on) {
    root.style.scrollBehavior = 'auto'; // theme.css asks for smooth, which would fight the eased steps
    window.addEventListener('wheel', function (e) {
      if (e.ctrlKey || e.defaultPrevented) return;          // pinch zoom, or handled elsewhere
      if (doc.body.style.overflow === 'hidden') return;    // a sheet has locked the page
      var dy = e.deltaY;
      if (!dy || Math.abs(e.deltaX) > Math.abs(dy)) return; // sideways swipes stay native
      if (scrollsInside(e.target, dy)) return;
      if (e.deltaMode === 1) dy *= 40;
      else if (e.deltaMode === 2) dy *= window.innerHeight;
      e.preventDefault();
      if (!smooth.moving) smooth.current = smooth.target = window.scrollY;
      smooth.target = clamp(smooth.target + dy, 0, maxScroll());
      smooth.moving = true;
      requestTick();
    }, { passive: false });

    // in-page links glide with the same easing
    doc.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var link = e.target.closest && e.target.closest('a[href^="#"]');
      var id = link && decodeURIComponent(link.getAttribute('href').slice(1));
      var el = id && doc.getElementById(id);
      if (!el) return;
      e.preventDefault();
      var pad = parseFloat(getComputedStyle(root).scrollPaddingTop) || 0;
      scrollToY(el.getBoundingClientRect().top + window.scrollY - pad);
      if (history.replaceState) history.replaceState(null, '', '#' + id);
      if (!el.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) el.setAttribute('tabindex', '-1');
      el.focus({ preventScroll: true });
    });
  }

  window.addEventListener('scroll', function () {
    // someone else moved the page (keyboard, scrollbar, find, a script): follow it
    if (smooth.moving && Math.abs(window.scrollY - smooth.current) > 2) smooth.moving = false;
    if (!smooth.moving) smooth.current = smooth.target = window.scrollY;
    requestTick();
  }, { passive: true });
  window.addEventListener('resize', requestTick, { passive: true });

  /* ── floating glass flips to dark glass over dark media ────
     Each island, the tab bar, the buy capsule and the deck rail adapt on their own,
     like iOS. Measured from the elements' real boxes, not with an IntersectionObserver
     band: rootMargin is ignored inside a cross-origin iframe (the theme editor), so the
     band covered the whole page there and turned the header text white. */
  var FLOATING = '.header .lg, .lg-tabbar__bar, [data-sticky-cta], [data-deck-rail]';
  var surfaces = [];
  var floating = [];

  function collectSurfaces() {
    surfaces = Array.prototype.slice.call(doc.querySelectorAll('[data-surface="dark"]'));
    floating = Array.prototype.slice.call(doc.querySelectorAll(FLOATING));
  }

  // true when at least half of a visible box lies over one of the dark boxes
  function overDark(b, darkBoxes) {
    var area = b.width * b.height;
    if (!area) return false;
    return darkBoxes.some(function (s) {
      var w = Math.min(b.right, s.right) - Math.max(b.left, s.left);
      var h = Math.min(b.bottom, s.bottom) - Math.max(b.top, s.top);
      return w > 0 && h > 0 && w * h >= area * 0.5;
    });
  }

  /* ── page effect: edge state, compact tab bar, dark glass ── */
  var tabbar = doc.querySelector('[data-lg-tabbar]');
  var lastY = window.scrollY;
  var pageY = 0;
  var darkFlags = [];
  effects.push({
    read: function () {
      pageY = window.scrollY;
      var darkBoxes = surfaces.map(function (el) { return el.getBoundingClientRect(); });
      darkFlags = floating.map(function (el) {
        return darkBoxes.length > 0 && overDark(el.getBoundingClientRect(), darkBoxes);
      });
    },
    write: function () {
      root.classList.toggle('is-scrolled', pageY > 8);
      if (tabbar) {
        if (pageY > lastY + 12 && pageY > 240) tabbar.classList.add('is-compact');
        else if (pageY < lastY - 8 || pageY < 120) tabbar.classList.remove('is-compact');
      }
      if (Math.abs(pageY - lastY) > 8) lastY = pageY;
      floating.forEach(function (el, k) { el.classList.toggle('is-on-dark', !!darkFlags[k]); });
    }
  });

  /* ── reveal content on scroll (never on glass or fixed UI) ─ */
  var REVEAL = [
    '.gx-head', '.gx-tile', '.video-demo', '.cmp', '.faq', '.cta-final .wrap', '.dims',
    '.payoff-bullet', '.trust', '.fn-card-v2', '.case', '.guarantee__pillars', '.specs-band__grid'
  ].join(',');
  function setupReveal(scope) {
    var all = (scope || doc);
    var panels = all.querySelectorAll('[data-reveal]');
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      panels.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    all.querySelectorAll(REVEAL).forEach(function (el) {
      if (el.closest('.lg, [data-sticky-cta], .gx-hero, .gx-page__head')) return;
      var siblings = el.parentElement ? Array.prototype.indexOf.call(el.parentElement.children, el) : 0;
      el.style.transitionDelay = Math.min(siblings, 5) * 70 + 'ms';
      el.classList.add('lg-reveal');
      io.observe(el);
    });
    // tool panels: the glass caption rises in once a third of the panel is on screen
    var panelIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        panelIo.unobserve(en.target);
      });
    }, { threshold: 0.33 });
    panels.forEach(function (el) { panelIo.observe(el); });
  }

  /* ── photo deck: sticky photos driven by scroll ──────────
     Desktop and landscape ('deck'): for each panel, p goes 0→1 while it rises into place and
     q goes 0→1 while the next panel slides over it. They become the grow, sink and parallax
     transforms, written straight to the frame, photo and caption, and only when they change.
     Progress is read from zero-height anchors in normal flow, because a stuck panel's own box
     no longer moves.
     Portrait screens ('side'): the cards sit in one row. The section pins (CSS, .is-sideways)
     and scrolling down slides the row sideways, resting on each card for a moment so one is
     always centred when the page stops; the cards beside it shrink and dim, a glass pill shows
     the count, and a sideways swipe jumps to the next card. With reduced motion ('swipe') the
     row stays a plain swipe gallery, which is also what shows without JS. */
  var deckPortrait = window.matchMedia('(max-aspect-ratio: 5/4)');
  var DECK_HOLD = 0.3; // share of each card's stretch of scrolling where it rests in the middle
  function pad2(k) { return (k < 10 ? '0' : '') + k; }
  function setupDeck(scope) {
    (scope || doc).querySelectorAll('[data-deck]').forEach(function (box) {
      if (box.__deck) return;
      var panels = Array.prototype.slice.call(box.querySelectorAll('[data-deck-panel]'));
      var anchors = Array.prototype.slice.call(box.querySelectorAll('[data-deck-anchor]'));
      var ticks = Array.prototype.slice.call(box.querySelectorAll('[data-deck-tick]'));
      var rail = box.querySelector('[data-deck-rail]');
      var pin = box.querySelector('[data-deck-pin]');
      var track = box.querySelector('[data-deck-track]');
      var bar = box.querySelector('[data-deck-bar]');
      var count = box.querySelector('[data-deck-count]');
      if (!panels.length || anchors.length !== panels.length) return;
      box.__deck = true;
      var parts = panels.map(function (el) {
        return {
          el: el,
          frame: el.querySelector('.gx-deck__frame'),
          img: el.querySelector('.gx-deck__img'),
          card: el.querySelector('.gx-deck__card'),
          p: -1,
          q: -1,
          d: -1
        };
      });
      var n = parts.length;
      var mode = '';
      var near = false;
      var vh = 0;
      var tops = [];
      var boxBottom = 0;
      var pinTop = 0;
      var pinRange = 1;
      var step = 0;
      var pos = -1;
      var active = 0;
      var loadedAll = false;

      function setActive(k) {
        if (k === active) return;
        active = k;
        if (count) count.textContent = pad2(k + 1);
        ticks.forEach(function (t, j) {
          if (j === k) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
      }

      // switch layouts: drop the other layout's inline motion and classes so its CSS applies cleanly
      function pickMode() {
        var next = deckPortrait.matches && pin && track ? (reduceMotion.matches ? 'swipe' : 'side') : 'deck';
        if (next === mode) return false;
        mode = next;
        parts.forEach(function (it) {
          it.p = it.q = it.d = -1;
          it.el.classList.remove('is-near', 'is-gone');
          it.el.style.transform = it.el.style.opacity = '';
          if (it.frame) it.frame.style.transform = '';
          if (it.img) it.img.style.transform = '';
          if (it.card) it.card.style.opacity = it.card.style.transform = it.card.style.visibility = '';
        });
        if (track) track.style.transform = '';
        if (bar) bar.style.transform = '';
        pos = -1;
        box.classList.toggle('is-sideways', mode === 'side');
        return true;
      }

      function writeSide() {
        if (rail) rail.classList.toggle('is-on', pinTop < vh * 0.5 && pinTop + pinRange + vh > vh * 0.5);
        if (!loadedAll) {
          // every photo is needed during the run, and the ones still off to the side would not load in time
          loadedAll = true;
          parts.forEach(function (it) { if (it.img) it.img.loading = 'eager'; });
        }
        var raw = clamp(-pinTop / pinRange, 0, 1) * (n - 1);
        var i = Math.min(Math.floor(raw), Math.max(0, n - 2));
        var g = clamp((raw - i - DECK_HOLD / 2) / (1 - DECK_HOLD), 0, 1);
        var p = n > 1 ? i + g * g * (3 - 2 * g) : 0;
        if (Math.abs(p - pos) < 0.0005) return;
        pos = p;
        if (track) track.style.transform = 'translate3d(' + (-p * step).toFixed(1) + 'px,0,0)';
        parts.forEach(function (it, k) {
          var d = Math.min(1, Math.abs(k - p));
          if (Math.abs(d - it.d) < 0.001) return;
          it.d = d;
          it.el.style.transform = 'scale(' + (1 - 0.07 * d).toFixed(4) + ')';
          it.el.style.opacity = (1 - 0.5 * d).toFixed(3);
          if (it.card) it.card.style.opacity = (1 - d).toFixed(3); // only the centred card's caption reads
        });
        if (bar) bar.style.transform = 'scaleX(' + (n > 1 ? p / (n - 1) : 1).toFixed(4) + ')';
        setActive(Math.round(p));
      }

      function writeDeck() {
        if (rail) rail.classList.toggle('is-on', tops[0] < vh * 0.6 && boxBottom > vh * 0.5);
        var ps = tops.map(function (t) { return clamp(1 - t / vh, 0, 1); });
        var current = 0;
        parts.forEach(function (it, i) {
          var p = ps[i];
          var q = i + 1 < ps.length ? ps[i + 1] : 0;
          if (p >= 0.5) current = i;
          // only panels on or near the screen keep GPU layers; fully covered ones are hidden
          it.el.classList.toggle('is-near', tops[i] < vh * 1.5 && q < 1);
          it.el.classList.toggle('is-gone', q >= 1);
          // start loading photos a few screens ahead, so a jump never lands on an empty frame
          if (tops[i] < vh * 3 && it.img && it.img.loading === 'lazy') it.img.loading = 'eager';
          if (reduceMotion.matches) return;
          if (Math.abs(p - it.p) < 0.0005 && Math.abs(q - it.q) < 0.0005) return;
          it.p = p;
          it.q = q;
          var c = clamp((p - 0.6) * 2.5, 0, 1);
          if (it.frame) it.frame.style.transform = 'translate3d(0,' + (-3 * q).toFixed(3) + '%,0) scale(' + (0.82 + 0.18 * p - 0.1 * q).toFixed(4) + ')';
          if (it.img) it.img.style.transform = 'translate3d(0,' + ((1 - p - q) * 6).toFixed(3) + '%,0)';
          if (it.card) {
            it.card.style.opacity = c.toFixed(3);
            it.card.style.transform = 'translate3d(0,' + ((1 - c) * 30).toFixed(1) + 'px,0)';
            it.card.style.visibility = c > 0 ? '' : 'hidden'; // no glass work for unseen captions
          }
        });
        setActive(current);
      }

      var fx = {
        read: function () {
          vh = window.innerHeight;
          var b = box.getBoundingClientRect();
          boxBottom = b.bottom;
          near = !(b.bottom < -vh || b.top > vh * 2);
          if (!near) return;
          if (mode === 'side') {
            var r = pin.getBoundingClientRect();
            pinTop = r.top;
            pinRange = Math.max(1, r.height - vh);
            step = n > 1 ? panels[1].offsetLeft - panels[0].offsetLeft : 0;
          } else if (mode === 'deck') {
            tops = anchors.map(function (a) { return a.getBoundingClientRect().top; });
          }
        },
        write: function () {
          if (pickMode()) {
            requestTick(); // measure the new layout next frame
            return;
          }
          if (!near) {
            if (rail) rail.classList.remove('is-on');
            return;
          }
          if (mode === 'side') writeSide();
          else if (mode === 'deck') writeDeck();
        }
      };

      function goTo(i) {
        i = clamp(i, 0, n - 1);
        if (parts[i].img) parts[i].img.loading = 'eager';
        if (mode === 'side') {
          var r = pin.getBoundingClientRect();
          var range = Math.max(1, r.height - window.innerHeight);
          scrollToY(r.top + window.scrollY + (n > 1 ? i / (n - 1) : 0) * range);
        } else if (mode === 'swipe') {
          track.scrollTo({ left: panels[i].offsetLeft - panels[0].offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        } else {
          scrollToY(anchors[i].getBoundingClientRect().top + window.scrollY);
        }
      }

      // pinned row: a sideways swipe moves one card (the page itself only scrolls up and down)
      if (pin) {
        var sx = 0;
        var sy = 0;
        var swiping = false;
        pin.addEventListener('pointerdown', function (e) {
          swiping = mode === 'side' && e.pointerType !== 'mouse';
          sx = e.clientX;
          sy = e.clientY;
        }, { passive: true });
        pin.addEventListener('pointerup', function (e) {
          if (!swiping) return;
          swiping = false;
          var dx = e.clientX - sx;
          var dy = e.clientY - sy;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.3) goTo(active + (dx < 0 ? 1 : -1));
        }, { passive: true });
        pin.addEventListener('pointercancel', function () { swiping = false; }, { passive: true });
      }

      ticks.forEach(function (t, i) { t.addEventListener('click', function () { goTo(i); }); });
      effects.push(fx);
      requestTick();
      box.__deckShow = function (panel) { goTo(Math.max(0, panels.indexOf(panel))); };
      box.__deckStop = function () {
        var k = effects.indexOf(fx);
        if (k > -1) effects.splice(k, 1);
        if (rail) rail.classList.remove('is-on');
      };
    });
  }

  /* ── sheets grow out of the button that opened them ───── */
  var origin = null;
  doc.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-cart-open], [data-mobile-nav-open]');
    if (!trigger) return;
    var r = trigger.getBoundingClientRect();
    origin = {
      x: e.clientX || r.left + r.width / 2,
      y: e.clientY || r.top + r.height / 2
    };
  }, true);

  function watchSheet(container, panelSelector) {
    if (!container) return;
    var panel = container.querySelector(panelSelector);
    new MutationObserver(function () {
      var open = container.classList.contains('is-open');
      if (open && panel) {
        var o = origin || { x: window.innerWidth - 40, y: 36 };
        panel.style.setProperty('--ox', (o.x - panel.offsetLeft) + 'px');
        panel.style.setProperty('--oy', (o.y - panel.offsetTop) + 'px');
        setTimeout(function () { panel.focus({ preventScroll: true }); }, 60);
      }
      var anyOpen = doc.querySelector('[data-cart-drawer].is-open, [data-mobile-nav].is-open');
      root.classList.toggle('lg-sheet-open', !!anyOpen);
      if (!open) origin = null;
    }).observe(container, { attributes: true, attributeFilter: ['class'] });
  }

  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var nav = doc.querySelector('[data-mobile-nav].is-open');
    var close = nav && nav.querySelector('[data-mobile-nav-close]');
    if (close) close.click();
  });

  /* ── hide the cart badge at zero ───────────────────────── */
  function syncCounts() {
    doc.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.toggleAttribute('data-zero', el.textContent.trim() === '0' || el.textContent.trim() === '');
    });
  }
  function watchCounts() {
    syncCounts();
    var mo = new MutationObserver(syncCounts);
    doc.querySelectorAll('[data-cart-count]').forEach(function (el) {
      mo.observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ── optional engines ─────────────────────────────────── */
  function loadEngines() {
    if (lite) return;
    if (root.getAttribute('data-glass-refraction') === 'true' && isChromium()) {
      loadScript(root.getAttribute('data-glass-refract'));
    }
    if (doc.querySelector('[data-lens-stage]')) {
      var start = function () { loadScript(root.getAttribute('data-glass-gl')); };
      if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1500 });
      else setTimeout(start, 600);
    }
  }

  function init() {
    collectSurfaces();
    requestTick();
    window.addEventListener('load', requestTick, { once: true }); // images can move the surfaces
    setupReveal();
    setupDeck();
    watchSheet(doc.querySelector('[data-cart-drawer]'), '.cart-drawer__panel');
    watchSheet(doc.querySelector('[data-mobile-nav]'), '.mobile-nav__panel');
    watchCounts();
    if (doc.readyState === 'complete') loadEngines();
    else window.addEventListener('load', loadEngines, { once: true });
  }

  /* theme editor: sections can be re-rendered in place */
  doc.addEventListener('shopify:section:load', function (e) {
    setupReveal(e.target);
    setupDeck(e.target);
    collectSurfaces();
    requestTick();
    if (window.GenxLens) window.GenxLens.init(e.target);
    if (window.GenxRefract) window.GenxRefract.refresh();
  });
  doc.addEventListener('shopify:section:unload', function (e) {
    if (window.GenxLens) window.GenxLens.destroy(e.target);
    e.target.querySelectorAll('[data-deck]').forEach(function (box) { if (box.__deckStop) box.__deckStop(); });
    // the section is still in the DOM during unload; measure after it is gone
    setTimeout(function () { collectSurfaces(); requestTick(); }, 0);
  });

  // theme editor: selecting a photo block scrolls to that photo
  doc.addEventListener('shopify:block:select', function (e) {
    var panel = e.target.closest && e.target.closest('[data-deck-panel]');
    var box = panel && panel.closest('[data-deck]');
    if (box && box.__deckShow) box.__deckShow(panel);
  });

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
